"use strict";
/**
* RestClient.ts
* Author: CALIB http://aka.ms/CALIB
* Copyright: Microsoft 2017
*
* Client helper for accessing ECS.
*/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var simplerestclients_1 = require("simplerestclients");
var SyncTasks = require("synctasks");
var Models_1 = require("./Models");
var TelemetryEvents = require("./TelemetryEvents");
var ForceFetchTimeout = (48 * 60 * 60 * 1000); // Fetch every 48 hours at a minimum
var EcsExpirationFallbackDuration = 30 * 60 * 1000; // 30 minutes
var EcsFetchDurationLimit = (365 * 24 * 60 * 60 * 1000); // fetch duration beyond 1 year is considered invalid
var EcsMinFetchTimeout = (30 * 1000); // Fail-safe lower bound for the fetchTimeout config parameter
var lastResponseTime;
var currentRequestTime;
// Fetches the ECS configuration from a specific ECS endpoint and project
var EcsFetcher = /** @class */ (function (_super) {
    __extends(EcsFetcher, _super);
    function EcsFetcher(url, _config, _skypeTokenData, _appActiveData, _allowBackgroundFetchData, _etag, _getUserConfig, parameters) {
        var _this = _super.call(this, url) || this;
        _this._config = _config;
        _this._skypeTokenData = _skypeTokenData;
        _this._appActiveData = _appActiveData;
        _this._allowBackgroundFetchData = _allowBackgroundFetchData;
        _this._etag = _etag;
        _this._getUserConfig = _getUserConfig;
        _this._apiPath = '/config/v1/' + _this._config.getConfig().clientName + '/' + _this._config.getConfig().clientVersion;
        if (parameters) {
            var paramList = Object.keys(parameters).map(function (key) {
                return encodeURIComponent(key) + '=' + encodeURIComponent(parameters[key].toString());
            });
            if (paramList) {
                _this._apiPath += '?' + paramList.join('&');
            }
        }
        return _this;
    }
    EcsFetcher.prototype._blockRequestUntil = function (options) {
        var _this = this;
        if (this._getUserConfig && !this._skypeTokenData.isSkypeTokenValid()) {
            console.log('ECS - Delaying User config fetch until we have a valid SkypeToken');
        }
        if (!this._appActiveData.isAppActive() && !this._allowBackgroundFetchData.isBackgroundFetchAllowed()) {
            var configType = this._getUserConfig ? 'User config' : 'config';
            console.log("ECS - Delaying " + configType + " fetch until the app is active (or background fetch is allowed)");
        }
        return SyncTasks.race([
            this._appActiveData.waitForAppActive(),
            this._allowBackgroundFetchData.waitForBackgroundFetchAllowed()
        ]).then(function () {
            if (_this._getUserConfig) {
                return _this._skypeTokenData.waitForValidSkypeToken();
            }
        }).then(function () {
            currentRequestTime = Date.now();
        });
    };
    EcsFetcher.prototype._getHeaders = function () {
        var headers = {};
        if (this._etag) {
            headers['If-None-Match'] = this._etag;
        }
        if (this._getUserConfig) {
            headers['Authorization'] = this._skypeTokenData.getSkypeToken();
        }
        return headers;
    };
    EcsFetcher.prototype.getConfig = function () {
        // Make sure the timeout is undefined, 0, or at least EcsMinFetchTimeout
        var timeout = this._config.getConfig().fetchTimeout;
        if (timeout !== undefined) {
            if (timeout < 0) {
                timeout = 0;
            }
            else if (timeout > 0 && timeout < EcsMinFetchTimeout) {
                timeout = EcsMinFetchTimeout;
            }
        }
        return _super.prototype.performApiGetDetailed.call(this, this._apiPath, { timeout: timeout });
    };
    return EcsFetcher;
}(simplerestclients_1.GenericRestClient));
// Main ECS client. Supports alternate ECS backend URLs and configuration fetch retries.
var EcsClient = /** @class */ (function () {
    function EcsClient(_config, _skypeTokenData, _appActiveData, _allowBackgroundFetchData, _telemetryManager) {
        this._config = _config;
        this._skypeTokenData = _skypeTokenData;
        this._appActiveData = _appActiveData;
        this._allowBackgroundFetchData = _allowBackgroundFetchData;
        this._telemetryManager = _telemetryManager;
        this._ecsFailureBackoffTimer = new simplerestclients_1.ExponentialTime(1000, 300000);
        this._cacheMaxAgeRegex = /.*max-age=(\d+).*/ig;
        this._serverIndex = NaN;
    }
    EcsClient.prototype.getConfig = function (oldConfig, userConfig, parameters) {
        var _this = this;
        var urls = this._config.getConfig().hosts;
        // Randomly select the ECS server url to issue the first request against and store the index
        if (isNaN(this._serverIndex) || this._serverIndex > urls.length - 1) {
            this._serverIndex = Math.floor(Math.random() * urls.length);
        }
        if (!urls || !urls.length) {
            return SyncTasks.Rejected(new Error('no configuration service endpoint'));
        }
        var deferred = SyncTasks.Defer();
        var fetchAttempts = 0;
        // Keep trying to fetch config (w/ Exponential Backoff) until it's fetched succesfully
        var callConfigServer = function () {
            var etag = oldConfig && oldConfig.eTag ? oldConfig.eTag : undefined;
            var fetcher = new EcsFetcher(urls[_this._serverIndex], _this._config, _this._skypeTokenData, _this._appActiveData, _this._allowBackgroundFetchData, etag, userConfig, parameters);
            fetchAttempts++;
            fetcher.getConfig().then(function (res) {
                var eTagHeader = res.headers['etag'];
                var expirationHeader = res.headers['expires'];
                if (res.body && res.body.Headers) {
                    // If the service headers are missing, try to get them from the response body
                    if (!eTagHeader) {
                        eTagHeader = res.body.Headers.ETag;
                    }
                    if (!expirationHeader) {
                        expirationHeader = res.body.Headers.Expires;
                    }
                }
                lastResponseTime = Date.now();
                // As per ECS guidance, if ETag or expiration are missing, treat as a failure.
                if (!eTagHeader || !expirationHeader) {
                    console.warn('ECS - Service returned an empty ETag or Expiration header: ' +
                        JSON.stringify(res));
                    return SyncTasks.Rejected();
                }
                // Override to deal with client clock skew.
                var updatedExpiration = new Date(_this._calculateExpirationDate(res.headers)).getTime();
                var ecsData;
                if (res.statusCode === 304 && oldConfig) {
                    // Etag matched, so no change. Use old config values and continue as normal.
                    // Use a shallow clone since we don't need to make a full clone of the config data.
                    ecsData = __assign({}, oldConfig);
                    ecsData.eTag = eTagHeader;
                    ecsData.expiration = updatedExpiration;
                    ecsData.cacheUpdateTime = lastResponseTime;
                    ecsData.lastFetchTokenHash = _this._skypeTokenData.getSkypeTokenHash(res.requestHeaders['Authorization']);
                }
                else {
                    if (typeof res.body !== 'object') {
                        console.warn('ECS - Service returned invalid response ' + JSON.stringify(res));
                        return SyncTasks.Rejected();
                    }
                    // Take from response
                    ecsData = {
                        config: res.body,
                        configType: userConfig ? Models_1.EcsConfigType.User : Models_1.EcsConfigType.Default,
                        eTag: eTagHeader,
                        expiration: updatedExpiration,
                        cacheUpdateTime: currentRequestTime,
                        lastFetchTokenHash: _this._skypeTokenData.getSkypeTokenHash(res.requestHeaders['Authorization']),
                        appVersion: _this._config.getConfig().clientVersion
                    };
                }
                var fetchDelayMs = lastResponseTime - currentRequestTime;
                var fetchDurationValid = (fetchDelayMs < 0 || fetchDelayMs > EcsFetchDurationLimit) ? false : true;
                var cacheAge = oldConfig ? (Date.now() - oldConfig.cacheUpdateTime) : 0;
                _this._telemetryManager.sendTelemetryEvent(new TelemetryEvents.EcsConfigFetchResponse(fetchDelayMs, res.statusCode, fetchAttempts, _this._skypeTokenData.isSkypeTokenValid(), urls[_this._serverIndex], eTagHeader, fetchDurationValid, etag ? true : false, JSON.stringify(res).length, cacheAge, (oldConfig && oldConfig.expiration - Date.now() > 0) ? false : true, currentRequestTime, lastResponseTime));
                _this._ecsFailureBackoffTimer.reset();
                deferred.resolve(ecsData);
            }).catch(function () {
                // Backoff and Switch ECS servers if possible.
                _this._serverIndex = (_this._serverIndex + 1) % (urls.length);
                setTimeout(function () {
                    callConfigServer();
                }, _this._ecsFailureBackoffTimer.getTimeAndCalculateNext());
            });
        };
        callConfigServer();
        return deferred.promise();
    };
    EcsClient.prototype._calculateExpirationDate = function (headers) {
        var forceFetchTime = Date.now() + ForceFetchTimeout;
        if (headers['date'] && headers['expires']) {
            // Calculate client clock skew
            var responseDate = new Date(headers['date']).getTime();
            var clockSkew = Date.now() - responseDate;
            // Ensure we don't do Math.min(forceFetchTime, NaN) => NaN
            var headerExpirationTime = new Date(headers['expires']).getTime();
            if (isNaN(headerExpirationTime)) {
                return forceFetchTime;
            }
            return Math.min(forceFetchTime, headerExpirationTime + clockSkew);
        }
        // This is a fix for browsers that don't implement Access-Control-Expose-Headers header properly
        // and Date header is not propagated (IE, Edge). We take the value from the cache-control
        // header (seconds) and add it to the client's current time.
        var match = this._cacheMaxAgeRegex.exec(headers['cache-control']);
        this._cacheMaxAgeRegex.lastIndex = 0;
        if (match && match.length === 2) {
            return Math.min(forceFetchTime, Date.now() + (Number(match[1]) * 1000));
        }
        // Backup - send next request in 30 minutes.
        return Date.now() + EcsExpirationFallbackDuration;
    };
    return EcsClient;
}());
exports.default = EcsClient;
